import React from 'react';
import classnames from 'classnames';
import { Alert } from 'tea-component';
import { useLocation, useIntl } from 'umi';
import { storage, getBasePath } from '@/utils/utils';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { useAd } from './useAd';
import './index.scss';

export interface MarqueeBannerProps {
    /** 是否需要顶部外边距 */
    isMarginTop?: boolean;
    /** 外部传入的额外类名 */
    className?: string;
    /** 关闭时的额外回调（关闭记录已由组件内部写入） */
    onClose?: () => void;
}

/** 关闭记录的 localStorage key 前缀（实际 key = 前缀 + 当前 URL，按页面隔离） */
const MARQUEE_CLOSED_KEY_PREFIX = '__db_intelligence_marquee_closed__';
/** 关闭后多久重新展示：7 天 */
const CLOSE_TTL = 7 * 24 * 60 * 60 * 1000;

/**
 * 跑马灯组件
 * - 通过 useAd hook 按当前页 URL + type='marquee' 拉取后端配置
 * - 命中数据时取数组第一条展示
 * - 关闭后当前页面 7 天内不展示
 * - 样式与 DataAlertTipApi 保持一致
 */
const MarqueeBanner: React.FC<MarqueeBannerProps> = (props) => {
    const { isMarginTop = false, onClose, className } = props;
    /** 自动获取当前页 URL（含 /v2 等 base 前缀），由 useLocation 触发路由变化时重渲染 */
    const { pathname } = useLocation();
    const { locale } = useIntl();
    const currentUrl = `${getBasePath()}${pathname}`;
    /** 拉取当前页跑马灯配置 */
    const { list } = useAd({ url: currentUrl, type: 'marquee' });
    /** 当前页面对应的 storage key */
    const closedStorageKey = `${MARQUEE_CLOSED_KEY_PREFIX}${currentUrl}`;
    /** 埋点 key，暂未引入分类，先固定占位 */
    const alertKey = 'marquee';
    /** 是否展示（storage 自带过期判断，过期会自动清除） */
    const getTipIsShow = (): boolean => !storage.getItem(closedStorageKey);
    /** 关闭：写入 7 天 TTL，仅影响当前页面 */
    const handleClose = () => {
        storage.setItem(closedStorageKey, 1, 'localStorage', Date.now() + CLOSE_TTL);
        onClose?.();
    };

    /** 事件委托：富文本里的 <a> 被点时上报埋点 */
    const handleLinkClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'A') {
            const buriedPointConfig =
                dbiBtnLogMes[alertKey as keyof typeof dbiBtnLogMes];
            const extInfoValue = (buriedPointConfig as any)?.extInfo;
            let normalizedExtInfo: { extInfo?: string } = {};
            if (
                extInfoValue &&
                typeof extInfoValue === 'object' &&
                !Array.isArray(extInfoValue)
            ) {
                normalizedExtInfo = extInfoValue;
            } else if (
                typeof extInfoValue === 'string' &&
                extInfoValue.trim().length
            ) {
                normalizedExtInfo = { extInfo: extInfoValue };
            }

            if (buriedPointConfig) {
                const logConfig = {
                    typeId: (buriedPointConfig as any).typeId,
                    pageId: buriedPointConfig.pageId,
                    buttonId: buriedPointConfig.buttonId,
                    buttonName: (buriedPointConfig as any).buttonName || alertKey,
                    extInfo: JSON.stringify({ ...normalizedExtInfo }),
                };
                reportButtonLog(logConfig);
            }
        }
    };

    /** 取当前 locale 对应的文案（中文取 text_cn，否则取 text_en） */
    const getItemText = (item: any): string => {
        const isZh = locale?.startsWith('zh');
        return (isZh ? item?.content?.text_cn : item?.content?.text_en) || '';
    };

    /** 渲染内容：把所有命中条目逐条渲染成 div，仅渲染有文案的条目 */
    const getContent = () => {
        return (
            <>
                {list
                    .filter((item) => !!getItemText(item))
                    .map((item, idx) => (
                        <div
                            key={item?.id ?? idx}
                            onClick={handleLinkClick}
                            dangerouslySetInnerHTML={{ __html: getItemText(item) }}
                        />
                    ))}
            </>
        );
    };

    /** 是否存在任意一条有效（含文案）的跑马灯 */
    const hasValidContent = list.some((item) => !!getItemText(item));
    // 没有任何有效内容 → 不展示
    if (!hasValidContent) return null;
    if (!getTipIsShow()) return null;

    return (
        <Alert
            defaultVisible
            className={classnames(
                'dataAlertTip-less',
                'data-alert-tip-api',
                'intel-marquee',
                className,
                { 'dataAlertTip-marginTop': isMarginTop },
            )}
            onClose={handleClose}
        >
            {getContent()}
        </Alert>
    );
};

export default MarqueeBanner;
